// Производное состояние для отображения. Сохранение и проверки выполняет сервер.
import type {
  Availability,
  BranchDay,
  BranchRule,
  CellColor,
  IntervalSpec,
  PlanPattern,
  Room,
  ScheduleState,
  TimeInterval,
} from '@/store/calendar/types';

export const DAY_MS = 86400000;
export const MAX_PATTERN_DAYS = 93;

export const WEEKDAY_NAMES = [
  'Понедельник',
  'Вторник',
  'Среда',
  'Четверг',
  'Пятница',
  'Суббота',
  'Воскресенье',
];

const TIME_RE = /^\d{2}:\d{2}$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// 'HH:MM' → минуты от начала суток, NaN для некорректного значения
export const toMinutes = (value: string): number => {
  if (typeof value !== 'string' || !TIME_RE.test(value)) return NaN;
  const hours = Number(value.slice(0, 2));
  const minutes = Number(value.slice(3));
  return hours < 24 && minutes < 60 ? hours * 60 + minutes : NaN;
};

export const toTime = (minutes: number): string => {
  const hours = String(Math.floor(minutes / 60)).padStart(2, '0');
  return `${hours}:${String(minutes % 60).padStart(2, '0')}`;
};

// 'YYYY-MM-DD' → UTC-миллисекунды, NaN для несуществующей даты
export const dateValue = (value: unknown): number => {
  if (typeof value !== 'string' || !DATE_RE.test(value)) return NaN;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === value ? ms : NaN;
};

export const isValidDate = (value: unknown): value is string => Number.isFinite(dateValue(value));

export const todayIso = (): string => new Date().toISOString().slice(0, 10);

// Полдень UTC — чтобы смещение часового пояса не перескакивало через сутки
const noon = (date: string): Date => new Date(`${date}T12:00:00Z`);

export const addDays = (date: string, days: number): string => (
  new Date(noon(date).getTime() + days * DAY_MS).toISOString().slice(0, 10)
);

export const formatDate = (date: string, options: Intl.DateTimeFormatOptions): string => (
  noon(date).toLocaleDateString('ru-RU', { timeZone: 'UTC', ...options })
);

export const formatDayTitle = (date: string): string => (
  formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' })
);

// 1 — понедельник … 7 — воскресенье
export const weekdayOf = (date: string): number => noon(date).getUTCDay() || 7;

export const isWeekend = (date: string): boolean => weekdayOf(date) > 5;

export const weekStart = (date: string): string => addDays(date, 1 - weekdayOf(date));

export const hoursText = (items: TimeInterval[]): string => (
  items.length ? items.map(({ start, end }) => `${start}–${end}`).join(', ') : 'нет'
);

export const durationMinutes = ({ start, end }: TimeInterval): number => (
  toMinutes(end) - toMinutes(start)
);

const contains = (outer: TimeInterval, inner: TimeInterval): boolean => (
  toMinutes(outer.start) <= toMinutes(inner.start) && toMinutes(outer.end) >= toMinutes(inner.end)
);

const overlaps = (
  a: TimeInterval & { date: string },
  b: TimeInterval & { date: string },
): boolean => (
  a.date === b.date
  && toMinutes(a.start) < toMinutes(b.end)
  && toMinutes(b.start) < toMinutes(a.end)
);

// Действующее на дату правило филиала — последнее по effectiveFrom
export const findBranchRule = (
  rules: BranchRule[],
  branchId: number | null,
  date: string,
): BranchRule | null => (
  rules
    .filter((rule) => rule.branchId === Number(branchId) && rule.effectiveFrom <= date)
    .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))[0] || null
);

export const getBranchDay = (
  rules: BranchRule[],
  branchId: number | null,
  date: string,
): BranchDay => {
  const weekday = weekdayOf(date);
  const rule = findBranchRule(rules, branchId, date);
  if (!rule) {
    return {
      branchId,
      date,
      weekday,
      open: [],
      intake: [],
      closed: true,
      configured: false,
      label: 'Режим работы не задан',
    };
  }
  const exception = (rule.exceptions || []).find((item) => item.date === date);
  const day = exception
    || rule.week.find((item) => item.weekday === weekday)
    || { open: [], intake: [] };
  return {
    branchId,
    date,
    weekday,
    open: day.open,
    intake: day.intake,
    closed: !day.open.length,
    intakeClosed: !day.intake.length,
    isException: !!exception,
    label: exception?.label || '',
    effectiveFrom: rule.effectiveFrom,
    configured: true,
  };
};

type AccessResult = { ok: true; message?: undefined } | { ok: false; message: string };

export const checkBranchAccess = (
  state: ScheduleState,
  rooms: Room[],
  spec: IntervalSpec,
): AccessResult => {
  const room = rooms.find((item) => item.id === Number(spec.roomId));
  if (!room) return { ok: false, message: 'Кабинет не найден.' };

  const day = getBranchDay(state.branchRules, room.branchId, spec.date);
  const inside = (list: TimeInterval[]) => list.some((item) => contains(item, spec));

  if (!day.configured) return { ok: false, message: 'Режим филиала не задан.' };
  if (day.closed) return { ok: false, message: 'Филиал закрыт.' };
  if (!inside(day.open)) {
    return { ok: false, message: 'Интервал за пределами рабочего времени филиала.' };
  }
  if (!inside(day.intake)) {
    return { ok: false, message: 'В этом интервале приём пациентов не ведётся.' };
  }
  return { ok: true };
};

const pickColor = (
  booked: boolean,
  closed: boolean,
  shared: boolean,
): CellColor => {
  if (booked) return 'red';
  if (closed) return 'gray';
  if (shared) return 'yellow';
  return 'green';
};

// Занятость кабинета на интервал: красный — пациент, серый — закрыт,
// жёлтый — в кабинете запланированы коллеги, зелёный — свободен
export const getAvailability = (
  state: ScheduleState,
  rooms: Room[],
  spec: IntervalSpec,
): Availability => {
  const roomId = Number(spec.roomId);
  const roomSlots = state.slots.filter((slot) => slot.roomId === roomId && overlaps(slot, spec));
  const slots = roomSlots.filter((slot) => slot.id !== Number(spec.excludeSlotId));
  const bookedSlots = roomSlots.filter(
    (slot) => state.appointments.some((item) => item.slotId === slot.id),
  );
  const bookedIds = new Set(bookedSlots.map((slot) => slot.id));
  const appointments = state.appointments.filter((item) => bookedIds.has(item.slotId));
  const staffIds = [...new Set(
    slots.filter((slot) => slot.staffId !== Number(spec.staffId)).map((slot) => slot.staffId),
  )];
  const roomBreaks = state.breaks.filter(
    (item) => item.scope === 'room' && item.roomId === roomId && overlaps(item, spec),
  );
  const branch = checkBranchAccess(state, rooms, spec);
  const color = pickColor(
    bookedSlots.length > 0,
    !branch.ok || roomBreaks.length > 0,
    staffIds.length > 0,
  );

  return {
    color,
    blocked: color === 'red' || color === 'gray',
    slots,
    bookedSlots,
    appointments,
    staffIds,
    roomBreaks,
    branchBlocked: !branch.ok,
    branchReason: branch.message || '',
  };
};

type PatternDatesSpec = {
  from: string;
  to: string;
  pattern: Exclude<PlanPattern, 'once'>;
  anchor?: string;
};

// Даты периода по правилу повторения; пустой массив — период некорректен
export const getPatternDates = (spec: PatternDatesSpec): string[] => {
  const from = dateValue(spec.from);
  const to = dateValue(spec.to);
  const anchor = dateValue(spec.anchor || spec.from);
  const count = Math.round((to - from) / DAY_MS) + 1;
  const { pattern } = spec;

  if (
    !Number.isFinite(from)
    || !Number.isFinite(to)
    || count < 1
    || count > MAX_PATTERN_DAYS
    || !['every', 'weekday', '2on2'].includes(pattern)
    || (pattern === '2on2' && !Number.isFinite(anchor))
  ) {
    return [];
  }

  return Array.from({ length: count }, (_, index) => from + index * DAY_MS)
    .filter((ms) => {
      const weekday = new Date(ms).getUTCDay();
      // Позиция в цикле «2 рабочих / 2 выходных» относительно даты начала цикла
      const cycle = ((Math.round((ms - anchor) / DAY_MS) % 4) + 4) % 4;
      if (pattern === 'weekday') return weekday !== 0 && weekday !== 6;
      if (pattern === '2on2') return cycle < 2;
      return true;
    })
    .map((ms) => new Date(ms).toISOString().slice(0, 10));
};
